import React, { useEffect, useRef, useState } from 'react';
import { LANDING_STATS } from './statsData';

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const styles = `
  /* Variables and font imports are handled by global.css */

  /* ── Section wrapper ── */
  .about-section {
    padding: 130px 60px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    align-items: center;
    border-top: 1px solid var(--border-soft);
    background: var(--black);
    overflow: hidden;
    position: relative;
  }

  /* Subtle bg glow */
  .about-section::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(ellipse 60% 50% at 20% 60%, rgba(245,200,0,0.04) 0%, transparent 60%);
  }

  /* ── VISUAL COLUMN ── */
  .about-visual {
    position: relative;
    height: 560px;
    opacity: 0;
    transform: translateX(-32px);
    transition: opacity 0.8s ease, transform 0.8s ease;
    will-change: opacity, transform;
  }
  .about-visual.visible {
    opacity: 1;
    transform: translateX(0);
  }

  /* Base block */
  .ab {
    position: absolute;
    border-radius: 3px;
  }

  /* Main watermark block */
  .ab-main {
    top: 0; left: 0;
    width: 73%; height: 61%;
    background: linear-gradient(135deg, #1c1900, #100f00);
    border: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    transition: transform 0.4s ease;
  }
  .ab-main:hover { transform: scale(1.015); }

  .ab-main-text {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 80px;
    color: rgba(245,200,0,0.08);
    letter-spacing: 6px;
    line-height: 1;
    text-align: center;
    user-select: none;
    transition: color 0.4s ease;
  }
  .ab-main:hover .ab-main-text {
    color: rgba(245,200,0,0.13);
  }

  /* Decorative grid overlay on ab-main */
  .ab-main::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(245,200,0,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(245,200,0,0.03) 1px, transparent 1px);
    background-size: 36px 36px;
    pointer-events: none;
  }

  /* Accent corner bracket on ab-main */
  .ab-main-corner {
    position: absolute;
    top: 16px; left: 16px;
    width: 24px; height: 24px;
    border-top: 1.5px solid rgba(245,200,0,0.3);
    border-left: 1.5px solid rgba(245,200,0,0.3);
  }
  .ab-main-corner-br {
    position: absolute;
    bottom: 16px; right: 16px;
    width: 24px; height: 24px;
    border-bottom: 1.5px solid rgba(245,200,0,0.3);
    border-right: 1.5px solid rgba(245,200,0,0.3);
  }

  /* Stat block */
  .ab-stat {
    bottom: 0; right: 0;
    width: 57%; height: 47%;
    background: var(--accent);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 26px 30px;
    transition: transform 0.4s ease, box-shadow 0.4s ease;
    overflow: hidden;
  }
  .ab-stat::before {
    content: '';
    position: absolute;
    top: -40px; right: -40px;
    width: 120px; height: 120px;
    border-radius: 50%;
    background: rgba(0,0,0,0.07);
  }
  .ab-stat:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(245,200,0,0.2);
  }

  .ab-stat-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 64px;
    color: var(--black);
    line-height: 1;
    position: relative;
    z-index: 1;
  }
  .ab-stat-label {
    font-size: 11px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: rgba(0,0,0,0.55);
    margin-top: 4px;
    position: relative;
    z-index: 1;
  }

  /* Animated counter */
  .ab-stat-num .count-value {
    display: inline-block;
    transition: opacity 0.3s;
  }

  /* Tag block */
  .ab-tag {
    bottom: 0; left: 0;
    width: 35%; height: 27%;
    background: var(--gold);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.4s ease;
  }
  .ab-tag:hover { transform: translateY(-4px); }

  .ab-tag-text {
    font-family: 'Instrument Serif', serif;
    font-style: italic;
    font-size: 17px;
    color: var(--black);
    text-align: center;
    padding: 16px;
    line-height: 1.45;
  }

  /* Floating badge on the visual */
  .ab-floating-badge {
    position: absolute;
    top: 50px; right: -18px;
    width: 90px; height: 90px;
    border-radius: 50%;
    background: var(--card-bg);
    border: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    z-index: 4;
    animation: rotateBadge 18s linear infinite;
    will-change: transform;
  }
  .ab-floating-badge-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 22px;
    color: var(--accent);
    line-height: 1;
  }
  .ab-floating-badge-text {
    font-size: 8px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: var(--muted-light);
    text-align: center;
  }

  /* Year label */
  .ab-year {
    position: absolute;
    top: 24px; right: 24px;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 11px;
    letter-spacing: 3px;
    color: var(--muted);
    z-index: 3;
  }

  /* ── CONTENT COLUMN ── */
  .about-content {
    opacity: 0;
    transform: translateX(32px);
    transition: opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s;
    will-change: opacity, transform;
  }
  .about-content.visible {
    opacity: 1;
    transform: translateX(0);
  }

  .section-tag {
    font-size: 10px;
    letter-spacing: 4px;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .section-tag::before {
    content: '';
    width: 24px; height: 1px;
    background: var(--accent);
    display: inline-block;
  }

  .about-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(40px, 4.5vw, 64px);
    line-height: 0.95;
    color: var(--white);
    margin-bottom: 28px;
  }
  .about-heading em {
    font-family: 'Instrument Serif', serif;
    font-style: italic;
    color: var(--gold);
  }

  .about-text {
    font-size: 15px;
    line-height: 1.9;
    color: var(--muted-light);
    margin-bottom: 36px;
    padding-left: 16px;
    border-left: 2px solid var(--border);
  }

  /* Stats row */
  .about-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0;
    margin-bottom: 36px;
    border: 1px solid var(--border-soft);
    border-radius: 4px;
    overflow: hidden;
  }

  .about-stat-item {
    padding: 16px 20px;
    border-right: 1px solid var(--border-soft);
    text-align: center;
    transition: background 0.3s;
  }
  .about-stat-item:last-child { border-right: none; }
  .about-stat-item:hover { background: rgba(245,200,0,0.03); }

  .astat-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 28px;
    color: var(--accent);
    line-height: 1;
  }
  .astat-label {
    font-size: 10px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: var(--muted);
    margin-top: 4px;
  }

  /* Feature items */
  .about-features {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .feature-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 18px 20px;
    border: 1px solid var(--border-soft);
    border-radius: 4px;
    transition: border-color 0.3s, background 0.3s, transform 0.25s;
    cursor: default;
    position: relative;
    overflow: hidden;
  }
  .feature-item::before {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 2px;
    background: var(--accent);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.35s ease;
  }
  .feature-item:hover {
    border-color: rgba(245,200,0,0.2);
    background: rgba(245,200,0,0.03);
    transform: translateX(4px);
  }
  .feature-item:hover::before { transform: scaleX(1); }

  .fsvg {
    width: 20px;
    height: 20px;
    color: var(--accent);
    flex-shrink: 0;
    margin-top: 2px;
    transition: transform 0.3s;
  }
  .feature-item:hover .fsvg { transform: scale(1.15); }
  .fsvg svg {
    width: 100%;
    height: 100%;
  }

  .feature-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--white);
    margin-bottom: 3px;
    letter-spacing: 0.3px;
  }

  .feature-body {
    font-size: 12px;
    color: var(--muted-light);
    line-height: 1.7;
  }

  /* CTA row */
  .about-cta {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-top: 28px;
    padding-top: 28px;
    border-top: 1px solid var(--border-soft);
  }

  .about-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--accent);
    color: var(--black);
    padding: 12px 28px;
    font-size: 11px;
    letter-spacing: 2px;
    text-transform: uppercase;
    text-decoration: none;
    border-radius: 2px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    transition: background 0.25s, transform 0.2s;
  }
  .about-btn-primary:hover { background: var(--gold); transform: translateY(-1px); }

  .about-btn-ghost {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--muted-light);
    font-size: 11px;
    letter-spacing: 2px;
    text-transform: uppercase;
    text-decoration: none;
    transition: color 0.25s, gap 0.25s;
  }
  .about-btn-ghost:hover { color: var(--white); gap: 14px; }

  /* Fade-up animation class (for intersection observer) */
  .fade-up {
    opacity: 0;
    transform: translateY(32px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .fade-up.visible {
    opacity: 1;
    transform: translateY(0);
  }

  @keyframes rotateBadge {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  /* Responsive */
  @media (max-width: 1024px) {
    .about-section {
      grid-template-columns: 1fr;
      padding: 90px 32px;
      gap: 52px;
    }
    .about-visual { height: 320px; }
    .ab-main-text { font-size: 56px; }
    .ab-stat-num  { font-size: 48px; }
    .about-content {
      transform: translateY(32px);
      transition: opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s;
    }
    .about-content.visible { transform: translateY(0); }
    .ab-floating-badge { display: none; }
  }

  @media (max-width: 640px) {
    .about-section  { padding: 72px 20px; gap: 40px; }
    .about-visual   { height: 260px; }
    .ab-main-text   { font-size: 40px; }
    .ab-stat-num    { font-size: 38px; }
    .about-stats    { grid-template-columns: 1fr 1fr 1fr; }
    .about-cta      { flex-direction: column; align-items: flex-start; }
    .about-text     { font-size: 14px; }
  }
`;


const STATS = [
  { num: LANDING_STATS.yearsExperience.toString(), suffix: '+', label: 'Years' },
  { num: LANDING_STATS.brands.toString(), suffix: '+', label: 'Brands' },
  { num: LANDING_STATS.clientSatisfaction.toString(), suffix: '%', label: 'Retained' },
];

const FEATURES = [
  {
    title: 'Results-First Mindset',
    body: 'Every strategy tied to a measurable business outcome. No vanity metrics.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
  },
  {
    title: 'Tailored Strategies',
    body: 'Custom solutions built around your goals, audience, and competitive landscape.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    title: 'Transparent Collaboration',
    body: "You're in the loop at every stage — no black boxes, no surprises.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
];

/* ─────────────────────────────────────────────
   HOOK — Intersection Observer
───────────────────────────────────────────── */
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
};

/* ─────────────────────────────────────────────
   ANIMATED COUNTER
───────────────────────────────────────────── */
const AnimatedCounter = ({ target, suffix = '', duration = 1800 }) => {
  const [count, setCount] = useState(0);
  const [ref, visible] = useInView(0.3);

  useEffect(() => {
    if (!visible) return;
    const numeric = parseInt(target.replace(/\D/g, ''), 10);
    if (!numeric) return;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * numeric));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [visible, target, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
};

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const LandingAbout = () => {
  const [visualRef, visualVisible] = useInView(0.1);
  const [contentRef, contentVisible] = useInView(0.1);

  return (
    <>
      <style>{styles}</style>

      <section className="about-section" id="about">

        {/* ── LEFT: Visual column ── */}
        <div
          ref={visualRef}
          className={`about-visual${visualVisible ? ' visible' : ''}`}
        >
          {/* Floating badge */}
          <div className="ab-floating-badge">
            <div className="ab-floating-badge-num">★</div>
            <div className="ab-floating-badge-text">Top Rated Agency</div>
          </div>

          {/* Year watermark */}
          <div className="ab-year">EST. 2025</div>

          {/* Main watermark block */}
          <div className="ab ab-main">
            <div className="ab-main-corner" />
            <div className="ab-main-corner-br" />
            <div className="ab-main-text">
              WEB<br />DIGIT<br />ING
            </div>
          </div>

          {/* Stat block */}
          <div className="ab ab-stat">
            <div className="ab-stat-num">
              <AnimatedCounter target={LANDING_STATS.brands.toString()} suffix="+" duration={1000} />
            </div>
            <div className="ab-stat-label">Brands Transformed</div>
          </div>

          {/* Tag block */}
          <div className="ab ab-tag">
            <div className="ab-tag-text">
              Results<br />over<br />everything.
            </div>
          </div>
        </div>

        {/* ── RIGHT: Content column ── */}
        <div
          ref={contentRef}
          className={`about-content${contentVisible ? ' visible' : ''}`}
        >
          {/* Section tag */}
          <div className="section-tag">Who We Are</div>

          {/* Heading */}
          <h2 className="about-heading">
            Not Just An Agency.<br />
            A <em>Growth Partner.</em>
          </h2>

          {/* Body text */}
          <p className="about-text">
            WebDigiting is a full-service digital agency built for brands that
            want to lead. We combine creative excellence with data-driven
            thinking to build digital presences that don't just look good —
            they perform.
          </p>

          {/* Mini stats row */}
          <div className="about-stats">
            {STATS.map(s => (
              <div className="about-stat-item" key={s.label}>
                <div className="astat-num">
                  {s.num}{s.suffix}
                </div>
                <div className="astat-label">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Feature list */}
          <div className="about-features">
            {FEATURES.map(f => (
              <div className="feature-item" key={f.title}>
                <div className="fsvg">{f.icon}</div>
                <div>
                  <div className="feature-title">{f.title}</div>
                  <div className="feature-body">{f.body}</div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA row */}
          <div className="about-cta">
            <a href="#services" className="about-btn-primary">
              Our Services
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
            <a href="#contact" className="about-btn-ghost">
              Work with us →
            </a>
          </div>
        </div>

      </section>
    </>
  );
};

export default LandingAbout;